import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { BroadcastStatusTone, broadcastStatusTone } from '../../models/BroadcastStatus';
import { User } from 'src/app/models/User';
import { AuthService } from '../../shared/services/auth.service';
import { ADMINISTRATOR } from '../../shared/services/user.service';
import { Broadcast } from './../../models/Broadcast';
import { PaginatedResult } from './../../shared/model/paginated-result';
import { BroadcastsService } from './../../shared/services/broadcasts.service';
import { ProviderAccountService } from 'src/app/shared/services/provider-account.service';
import { ProviderAccount } from 'src/app/models/ProviderAccount';
import { SearchSelectOption } from 'src/app/shared/components/search-select/search-select.component';

/**
 * Analisar SMS Massivo, layout v2 (CU-17tm466nzee, 14/10/2026) — busca global de envios de
 * TODOS os clientes (admin), com filtro por provedor e texto da mensagem.
 *
 * Regras que não mudam (só o layout mudou — ver `broadcasts-filter.component.html`):
 * - `GET /all-broadcasts` via `BroadcastsService.getAllBroadcasts`, com os mesmos parâmetros
 *   (`start_date`/`end_date`/`author` (nome)/`message`/`orderBy`/`invertedOrder`/
 *   `provider_account_id`).
 * - `onFilter` continua chamando o serviço direto (sem navegar pela URL) e `pageChanged`
 *   continua navegando com `queryParamsHandling: 'merge'` — mesma assimetria de hoje, não é
 *   bug desta tela para corrigir aqui.
 * - Provedor é seleção única (o `<select>` de hoje já era single-select) — virou
 *   `app-search-select` com a mesma semântica.
 * - Botão de ver detalhe continua navegando para `/broadcasts/view/:id` (tela de detalhe do
 *   SMS Massivo já migrada).
 */
@Component({
  selector: 'app-broadcasts-filter',
  templateUrl: './broadcasts-filter.component.html',
  styleUrls: ['./broadcasts-filter.component.scss'],
})
export class BroadcastsFilterComponent implements OnDestroy, OnInit {
  messageBroadcast: string;
  name: string;
  providerAccountId: string;
  perPage = 10;
  text: string;
  /** 'YYYY-MM-DD' de `<input type="date">` — igual ao formato que o filtro já espera. */
  startedMonth: string;
  endedMonth: string;
  startDate: string;
  endDate: string;
  invalidTime: boolean;
  orderBy = 'created_at';
  invertedOrder = true;
  page: number;
  pageSize = 10;
  user: User;
  loading: boolean;
  result: PaginatedResult<Broadcast[]>;
  providers: ProviderAccount[] = [];
  providerOptions: SearchSelectOption[] = [];
  subscriptions: Subscription[] = [];


  get administrator() {
    return this.user.profile_id === ADMINISTRATOR;
  }

  constructor(
    private broadcastsService: BroadcastsService,
    private router: Router,
    private route: ActivatedRoute,
    private providerAccountService: ProviderAccountService,
    private authService: AuthService,
    private activatedRoute: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.user = this.authService.getLoggedInUser();

    this.subscriptions.push(
      this.route.queryParams.subscribe((params) => {
        this.page = params.page || this.page;
        this.pageSize = params.page_size || this.pageSize;
        this.startDate = params.started_To_Date ?? '';
        this.endDate = params.ended_To_Date ?? '';
        this.messageBroadcast = params.message || '';
        this.orderBy = params.orderBy || this.orderBy;
        this.invertedOrder = params.invertedOrder === 'true' || this.invertedOrder;
        this.name = params.name || '';
        this.providerAccountId = params.provider_account_id || '';
        this.listPaginated(this.startDate, this.endDate, this.name, this.providerAccountId, this.messageBroadcast);
      }),
    );

    this.subscriptions.push(
      this.providerAccountService.listAll()
        .subscribe(providerAccounts => {
          this.providers = providerAccounts;
          this.providerOptions = providerAccounts.map(provider => ({ value: provider.id, label: provider.name }));
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  listPaginated(startDate = null, endDate = null, name = '', providerAccountId = '', messageBroadcast = ''): void {

    if (!startDate && this.startedMonth) {
      startDate = this.startedMonth;
    }

    if (!endDate && this.endedMonth) {
      endDate = this.endedMonth;
    }

      this.loading = true;
      this.subscriptions.push(
        this.broadcastsService
          .getAllBroadcasts(
            this.page,
            this.pageSize,
            startDate,
            endDate,
            messageBroadcast,
            this.orderBy,
            this.invertedOrder,
            name,
            providerAccountId
          )
          .pipe(finalize(() => (this.loading = false)))
          .subscribe((result) => {
            this.result = result;
          })
      );
  }

  onFilter(): void {
    this.page = 1;
    const queryParams = {
      page: this.page,
      per_page: this.perPage,
      message: this.messageBroadcast,
      name: this.name,
      provider_account_id: this.providerAccountId,
      start_date: this.startedMonth ?? '',
      end_date: this.endedMonth ?? '',
      orderBy: this.orderBy,
      invertedOrder: this.invertedOrder,
    };

    this.broadcastsService.getAllBroadcasts(
        this.page,
        this.perPage,
        queryParams.start_date,
        queryParams.end_date,
        queryParams.message,
        queryParams.orderBy,
        queryParams.invertedOrder,
        queryParams.name,
        queryParams.provider_account_id,
      ).subscribe((response) => {
        this.result = response;
      });

  }

  onProviderChange(providerAccountId: string | null): void {
    this.providerAccountId = providerAccountId || '';
  }

  statusTone(broadcast: Broadcast): BroadcastStatusTone {
    return broadcastStatusTone(broadcast.broadcast_status_id);
  }

  isCancelled(broadcast: Broadcast): boolean {
    return this.broadcastsService.isCancelled(broadcast);
  }

  pageChanged(page: number): void {
    this.page = page;
    const queryParams = {
      page: this.page,
      per_page: this.perPage,
      message: this.messageBroadcast,
      name: this.name,
      provider_account_id: this.providerAccountId,
      start_date: this.startedMonth ?? '',
      end_date: this.endedMonth ?? '',
      orderBy: this.orderBy,
      invertedOrder: this.invertedOrder,
    };

    this.router.navigate([], { queryParams, queryParamsHandling: 'merge' });
  }

  orderByChanged() {
    this.onFilter(), {
      relativeTo: this.activatedRoute,
      queryParams: {
        orderBy: this.orderBy,
        startedMonth: this.startedMonth,
        endedMonth: this.endedMonth,
      },
      queryParamsHandling: 'merge',
    };
  }

  invertOrder(): void {
    this.invertedOrder = !this.invertedOrder;
    this.onFilter(), {
      relativeTo: this.activatedRoute,
      queryParams: {
        invertedOrder: this.invertedOrder,
        startedMonth: this.startedMonth,
        endedMonth: this.endedMonth
      },
      queryParamsHandling: 'merge'
    }
  }

}
